import React, { useState } from 'react';
import { AlertTriangle, Trash2, Loader2, CheckCircle2, RotateCcw, Bot } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { adminApi, ApiError } from '../../lib/api';

interface ResetResult {
  pattern_performance: boolean;
  positions_deleted: number;
  daily_reports_deleted: number;
  in_memory_cleared: boolean;
  cleared_at: string;
}

interface PurgeResult {
  deleted: number;
  kept: number;
  total_before: number;
}

const DangerZoneTab: React.FC = () => {
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Purge bots state (separate flow from master reset)
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeResult, setPurgeResult] = useState<PurgeResult | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [purgeWarRoomOnly, setPurgeWarRoomOnly] = useState(false);

  const canConfirm = confirmText === 'RESET';

  const handlePurge = async () => {
    setPurgeLoading(true);
    setPurgeError(null);
    setPurgeResult(null);
    try {
      const res = await adminApi.purgeBots({ keepActive: true, warRoomOnly: purgeWarRoomOnly });
      setPurgeResult(res);
    } catch (e: any) {
      setPurgeError(e instanceof ApiError ? e.detail : (e.message || String(e)));
    } finally {
      setPurgeLoading(false);
    }
  };

  const handleReset = async () => {
    if (!canConfirm) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminApi.resetHistory();
      setResult(res);
      setConfirmText('');
      setShowForm(false);
    } catch (e: any) {
      setError(e instanceof ApiError ? e.detail : e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-rose-500" />
        <h2 className="text-lg font-semibold">Danger Zone</h2>
        <Badge className="bg-rose-500/10 text-rose-600 border-rose-500/20">Irreversible</Badge>
      </div>

      {result && (
        <Alert className="border-emerald-500/30 bg-emerald-500/5">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          <AlertDescription className="space-y-1">
            <p className="font-medium text-emerald-700">Reset complete</p>
            <ul className="text-sm text-muted-foreground space-y-0.5 mt-1">
              <li>Pattern performance stats: {result.pattern_performance ? 'cleared' : 'skipped'}</li>
              <li>Position records deleted: {result.positions_deleted}</li>
              <li>Daily reports deleted: {result.daily_reports_deleted}</li>
              <li>In-memory state cleared: {result.in_memory_cleared ? 'yes' : 'no (controller not running)'}</li>
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ── Purge Inactive Bots ─────────────────────────────────────── */}
      <Card className="border-amber-500/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-amber-600" />
            <CardTitle className="text-base">Purge Inactive Bots</CardTitle>
          </div>
          <CardDescription>
            Delete pattern documents that aren't currently active.  Use this to
            clean up the hundreds of stale bots accumulated from previous War Room
            runs before automatic cleanup was in place.  Active bots are preserved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {purgeResult && (
            <Alert className="border-emerald-500/30 bg-emerald-500/5">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <AlertDescription>
                Deleted <strong>{purgeResult.deleted}</strong> bot(s),
                kept <strong>{purgeResult.kept}</strong>
                {' '}({purgeResult.total_before} total before purge).
              </AlertDescription>
            </Alert>
          )}
          {purgeError && (
            <Alert variant="destructive">
              <AlertDescription>{purgeError}</AlertDescription>
            </Alert>
          )}
          <div className="flex items-center gap-3 text-sm">
            <input
              id="purge-war-room-only"
              type="checkbox"
              checked={purgeWarRoomOnly}
              onChange={(e) => setPurgeWarRoomOnly(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="purge-war-room-only" className="cursor-pointer">
              Only delete War Room bots (recommended) — leaves manual / template / AI Discovery bots untouched
            </Label>
          </div>
          <Button
            variant="outline"
            className="border-amber-500/30 text-amber-700 hover:bg-amber-500/10"
            disabled={purgeLoading}
            onClick={handlePurge}
          >
            {purgeLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            {purgeLoading ? 'Purging…' : 'Purge Inactive Bots Now'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-rose-500/20">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-rose-500" />
            <CardTitle className="text-base">Reset Trade History &amp; P&amp;L</CardTitle>
          </div>
          <CardDescription>
            Permanently deletes all historical trade records, pattern performance statistics,
            and daily P&amp;L reports. Bot configurations and credentials are unaffected.
            Open positions are not touched.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/10">
              <p className="font-medium text-rose-700">Will be deleted</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground">
                <li>• Pattern win/loss stats</li>
                <li>• Per-trade P&amp;L records</li>
                <li>• Daily performance reports</li>
                <li>• Closed position history</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg bg-muted/40 border border-border">
              <p className="font-medium">Preserved</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground">
                <li>• Bot configurations</li>
                <li>• API credentials</li>
                <li>• Risk settings</li>
                <li>• War Room settings</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg bg-muted/40 border border-border">
              <p className="font-medium">Not touched</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground">
                <li>• Open positions</li>
                <li>• Active orders</li>
                <li>• Broker connection</li>
                <li>• Audit trail</li>
              </ul>
            </div>
          </div>

          {!showForm ? (
            <Button
              variant="outline"
              className="border-rose-500/30 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700"
              onClick={() => setShowForm(true)}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Begin Reset
            </Button>
          ) : (
            <div className="space-y-3 p-4 rounded-lg border border-rose-500/20 bg-rose-500/5">
              <p className="text-sm font-medium text-rose-700">
                Type <span className="font-mono bg-rose-100 px-1 rounded">RESET</span> to confirm
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label className="sr-only">Confirmation</Label>
                  <Input
                    placeholder="Type RESET to confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className={canConfirm ? 'border-rose-500' : ''}
                    autoFocus
                  />
                </div>
                <Button
                  variant="destructive"
                  disabled={!canConfirm || loading}
                  onClick={handleReset}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  {loading ? 'Resetting...' : 'Confirm Reset'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={loading}
                  onClick={() => { setShowForm(false); setConfirmText(''); }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DangerZoneTab;
